import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useRanking, rankImg, fmtBaht, bonusText, scopeText, goalText, turnPct, whenText, STATE_LABEL } from "../rankingLogic";
import { Empty, LoginGate, Skeleton } from "../ui";

/*
 * หน้า "แรงค์สมาชิก" — ดีไซน์ landing-ver2 (นีออน): วงแหวนความคืบหน้าเรืองแสงรอบเหรียญ · การ์ดแต่ละแรงค์เลื่อนแนวนอน
 * (ตรรกะอยู่ที่ rankingLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์ละครสัตว์แทน)
 */

function RankUp({ result, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!result) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [result, onClose]);
  if (!result) return null;
  const ok = result.ok;
  return (
    <div className="rkn-overlay" role="dialog" aria-modal="true" aria-label={ok ? "เลื่อนแรงค์สำเร็จ" : "รับโบนัสไม่สำเร็จ"} onClick={onClose}>
      <div className="rkn-up" onClick={(e) => e.stopPropagation()}>
        {ok ? (
          <>
            <div className="rkn-up-rays" aria-hidden="true" />
            <img className="rkn-up-medal" src={rankImg(result.rank && result.rank.image_url)} alt="" />
            <div className="rkn-up-kicker">
              <Icon icon="solar:cup-star-bold" width={16} /> RANK UP
            </div>
            <div className="rkn-up-name">{result.rank ? result.rank.name : ""}</div>
            {result.amount > 0 && <div className="rkn-up-amt">+{fmtBaht(result.amount)}</div>}
            <ul className="rkn-up-notes">
              {result.amount > 0 && (
                <li>
                  <Icon icon="solar:check-circle-bold" width={16} /> โบนัสเข้ากระเป๋าของคุณแล้ว
                </li>
              )}
              {result.turnover_required > 0 && (
                <li className="warn">
                  <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} /> ต้องแทงให้ครบ {fmtBaht(result.turnover_required)} ก่อนถอนโบนัสส่วนนี้
                </li>
              )}
              {result.play_label && (
                <li className="warn">
                  <Icon icon="solar:ticket-bold" width={16} /> โบนัสนี้แทงได้{result.play_label}
                </li>
              )}
              {result.pending_left > 0 && (
                <li>
                  <Icon icon="solar:cup-star-bold" width={16} /> ยังมีโบนัสแรงค์ถัดไปรอรับอีก {result.pending_left} ขั้น
                </li>
              )}
            </ul>
          </>
        ) : (
          <>
            <div className="rkn-up-kicker err">
              <Icon icon="solar:shield-warning-bold" width={18} /> รับโบนัสไม่สำเร็จ
            </div>
            <p className="rkn-up-err">{result.message}</p>
          </>
        )}
        <button type="button" ref={closeRef} className="rkn-btn" onClick={onClose}>
          ตกลง
        </button>
      </div>
    </div>
  );
}

export default function RankingPage({ onDone }) {
  const nav = useNavigate();
  const rk = useRanking(onDone);
  const { view } = rk;
  const curRef = useRef(null);

  // เปิดหน้าแล้วเลื่อนแถบแรงค์ให้การ์ดแรงค์ปัจจุบันอยู่กลางจอ
  const curClass = view.current ? view.current.rankClass : null;
  useEffect(() => {
    // เลื่อนเฉพาะในแถบ (ไม่ใช้ scrollIntoView เพราะจะดึงทั้งหน้าเลื่อนตามไปด้วย)
    const el = curRef.current;
    const box = el && el.parentElement;
    if (box) box.scrollLeft = el.offsetLeft - (box.clientWidth - el.clientWidth) / 2;
  }, [curClass, rk.loading]);

  if (!rk.isAuthenticated) {
    return (
      <LoginGate
        icon="solar:cup-star-bold-duotone"
        title="เข้าสู่ระบบเพื่อดูแรงค์ของคุณ"
        desc="ฝากสะสมเพื่อเลื่อนแรงค์ แล้วรับโบนัสเลื่อนระดับทุกขั้น"
        perks={["ดูความคืบหน้าสู่แรงค์ถัดไป", "รับโบนัสเลื่อนแรงค์", "ดูประวัติการรับ"]}
        onLogin={() => nav("/auth")}
      />
    );
  }
  if (rk.loading) return <Skeleton h={320} style={{ marginTop: 18 }} />;

  const cur = view.current;
  const pct = Math.round(view.progress_pct);

  return (
    <div className="rkn-page">
      {/* ---- วงแหวนนีออนรอบเหรียญ ---- */}
      <section className={`rkn-hero${rk.enabled ? "" : " is-off"}`} aria-label="แรงค์ปัจจุบัน">
        <div className="rkn-glow" aria-hidden="true" />
        <div className="rkn-ring" style={{ "--p": `${rk.enabled ? view.progress_pct : 0}%` }}>
          <div className="rkn-ring-in">{cur && cur.image_url ? <img src={rankImg(cur.image_url)} alt={cur.name} /> : <Icon icon="solar:cup-star-bold-duotone" width={80} />}</div>
          {rk.enabled && view.next && <span className="rkn-pct">{pct}%</span>}
        </div>
        <div className="rkn-cur">
          <span className="rkn-cur-label">แรงค์ปัจจุบัน</span>
          <h1>{cur ? cur.name : "สมาชิก"}</h1>
        </div>
        {rk.enabled ? (
          <>
            <p className="rkn-goal">{goalText(view)}</p>
            <div className="rkn-chips">
              <span>
                <Icon icon="solar:wallet-money-bold-duotone" width={16} /> ฝากสะสม {fmtBaht(view.deposit)}
              </span>
              {view.next && (
                <span>
                  <Icon icon="solar:flag-bold-duotone" width={16} /> เป้า {fmtBaht(view.next.threshold)}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="rkn-off">
            <Icon icon="solar:lock-keyhole-minimalistic-bold" width={18} /> ระบบแรงค์ปิดให้บริการชั่วคราว
          </div>
        )}
      </section>

      {/* ---- โบนัสรอรับ ---- */}
      {rk.enabled && view.pending.length > 0 && (
        <section className="rkn-sec">
          <h2>
            <Icon icon="solar:gift-bold-duotone" width={20} /> โบนัสรอรับ <b>{view.pending.length}</b>
          </h2>
          <div className="rkn-pend">
            {view.pending.map((p) => (
              <article className={`rkn-card${p.can_claim ? "" : " is-wait"}`} key={p.id}>
                <img src={rankImg(p.image_url)} alt="" />
                <div className="rkn-card-body">
                  <b>
                    {p.from} <Icon icon="solar:alt-arrow-right-bold" width={14} /> {p.to}
                  </b>
                  <span>{p.bonus > 0 ? bonusText(p) : "เลื่อนแรงค์ (ไม่มีโบนัส)"}</span>
                  {p.bonus > 0 && scopeText(p) && <em>{scopeText(p)}</em>}
                </div>
                <button type="button" className="rkn-btn sm" disabled={!p.can_claim || rk.claimingId === p.id} onClick={() => rk.claim(p)}>
                  <Icon icon={rk.claimingId === p.id ? "svg-spinners:ring-resize" : "solar:gift-bold"} width={18} />
                  {rk.claimingId === p.id ? "กำลังรับ..." : p.can_claim ? "รับโบนัส" : "รับขั้นก่อนหน้า"}
                </button>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ---- แถบแรงค์เลื่อนแนวนอน ---- */}
      <section className="rkn-sec">
        <h2>
          <Icon icon="solar:ranking-bold-duotone" width={20} /> เส้นทางแรงค์
        </h2>
        <div className="rkn-track" role="list">
          {view.ladder.map((r) => (
            <article key={r.rankClass} role="listitem" ref={r.state === "CURRENT" ? curRef : null} className={`rkn-tier st-${String(r.state).toLowerCase()}`}>
              <span className="rkn-tier-state">{STATE_LABEL[r.state]}</span>
              <img src={rankImg(r.image_url)} alt="" loading="lazy" />
              <b>{r.name}</b>
              <span className="rkn-tier-need">{r.rankClass === 0 ? "เริ่มต้น" : `ฝากครบ ${fmtBaht(r.threshold)}`}</span>
              {r.bonus > 0 ? <span className="rkn-tier-bonus">{bonusText(r)}</span> : <span className="rkn-tier-bonus dim">{r.rankClass === 0 ? "ทุกคนเริ่มที่นี่" : "ไม่มีโบนัส"}</span>}
              {r.bonus > 0 && scopeText(r) && <em>{scopeText(r)}</em>}
              {r.perk_text && <span className="rkn-tier-perk">{r.perk_text}</span>}
            </article>
          ))}
        </div>
      </section>

      {view.active.length > 0 && (
        <section className="rkn-sec">
          <h2>
            <Icon icon="solar:shield-keyhole-bold-duotone" width={20} /> โบนัสที่ยังมีเงื่อนไข
          </h2>
          <div className="rkn-actives">
            {view.active.map((a) => (
              <article className="rkn-active" key={a.id}>
                <div className="rkn-meter" style={{ "--p": `${a.locked ? turnPct(a) : 100}%` }} aria-hidden="true">
                  <b>{a.locked ? `${Math.round(turnPct(a))}%` : <Icon icon="solar:ticket-bold" width={18} />}</b>
                </div>
                <div className="rkn-active-body">
                  <div>
                    <code>แรงค์ {a.name}</code>
                    <strong>{fmtBaht(a.amount)}</strong>
                  </div>
                  {a.locked && (
                    <span>
                      ทำเทิร์นแล้ว {fmtBaht(a.turnover_done)} จาก {fmtBaht(a.turnover_required)}
                    </span>
                  )}
                  {a.restricted_left > 0 && (
                    <span>
                      เหลือ {fmtBaht(a.restricted_left)} · {a.play_label}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="rkn-sec">
        <h2>
          <Icon icon="solar:history-bold-duotone" width={20} /> ประวัติการรับแรงค์
        </h2>
        {view.history.length === 0 ? (
          <div className="lx-card">
            <Empty icon="solar:cup-star-bold-duotone">ยังไม่เคยเลื่อนแรงค์</Empty>
          </div>
        ) : (
          <ol className="rkn-timeline">
            {view.history.map((h) => (
              <li key={h.id}>
                <span className="rkn-dot">
                  <Icon icon="solar:check-read-bold" width={14} />
                </span>
                <img src={rankImg(h.image_url)} alt="" />
                <div className="rkn-tl-body">
                  <b>
                    {h.from} → {h.to}
                  </b>
                  <span>{whenText(h.received_at)}</span>
                </div>
                <strong>{h.amount > 0 ? `+${fmtBaht(h.amount)}` : "-"}</strong>
              </li>
            ))}
          </ol>
        )}
      </section>

      <RankUp result={rk.result} onClose={rk.closeResult} />
    </div>
  );
}
